import LegalLayout from "@/components/legal/LegalLayout";

const SECTIONS = [
  {
    t: "1. Information We Collect",
    children: [
      { sub: "Personal Information", b: "We may collect personal identification information from users in various ways, including but not limited to when users visit our App, register on the App, place an order, fill out a form, respond to a survey, or otherwise interact with our services. Personal information collected may include name, email address, phone number, and other information provided by users." },
      { sub: "Non-personal Information", b: "We may collect non-personal information about users whenever they interact with our App. Non-personal information may include the type of device used, operating system, browser type, Internet service provider, and other technical information." },
      { sub: "1.1 Personal Information", b: "When you create an account or use PamaPay, we may collect personal information, including but not limited to:", list: ["Name", "Email address", "Phone number", "Date of birth", "Identification documents (e.g., driver's licence, passport)"] },
      { sub: "1.2 Financial Information", b: "To facilitate transactions, we may collect financial information, such as:", list: ["Transaction history", "Bank account details (for certain services)"] },
      { sub: "1.3 Usage Data", b: "We may collect information about how you use our platform, including:", list: ["Device information (e.g., IP address, browser type, operating system)", "Log data (e.g., access times, pages viewed, errors)", "Interaction data (e.g., clicks, navigation patterns)"] },
      { sub: "1.4 Cookies and Tracking Technologies", b: "We use cookies and similar tracking technologies to collect data about your interactions with our platform. This helps us enhance user experience and analyse usage patterns." },
    ],
  },
  {
    t: "2. How We Use Your Information",
    b: "We use the information we collect for various purposes, including:",
    list: [
      "To provide and maintain our services",
      "To verify your identity and prevent fraud",
      "To process transactions and send transaction confirmations",
      "To communicate with you, including responding to inquiries and providing customer support",
      "To improve our platform and develop new features",
      "To comply with legal and regulatory requirements",
    ],
  },
  {
    t: "3. How We Share Your Information",
    children: [
      { sub: "3.1 With Service Providers", b: "We may share your information with third-party service providers who perform services on our behalf, such as payment processing, identity verification, and data analytics." },
      { sub: "3.2 For Legal Compliance", b: "We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, government agencies)." },
      { sub: "3.3 Business Transfers", b: "In the event of a merger, acquisition, or asset sale, your information may be transferred as part of the transaction. We will notify you of any such change in ownership or control." },
    ],
  },
  { t: "4. Security of Your Information", b: "We implement a variety of security measures to protect your information from unauthorised access, use, or disclosure. These measures include encryption, secure socket layer (SSL) technology, and regular security assessments. However, no method of transmission over the internet or electronic storage is completely secure." },
  {
    t: "5. Your Rights and Choices",
    children: [
      { sub: "5.1 Access and Update", b: "You have the right to access and update your personal information. You can review and edit your account information by logging into your account." },
      { sub: "5.2 Data Deletion", b: "You may request the deletion of your account and personal information by sending a mail to hello@akkayglobal.com. Please note that we may retain certain information as required by law or for legitimate business purposes." },
      { sub: "5.3 Opt-Out", b: "You may opt out of receiving promotional communications from us by following the unsubscribe instructions provided in the communications or by contacting us directly." },
    ],
  },
  { t: "6. International Data Transfers", b: "Your information may be transferred to and maintained on servers located outside your state, province, country, or other governmental jurisdiction where data protection laws may differ from those of your jurisdiction. We take appropriate steps to ensure that your data is treated securely and in accordance with this Privacy Policy." },
  { t: "7. Children's Privacy", b: "Our platform is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information." },
  { t: "8. Changes to This Privacy Policy", b: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on our platform. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page." },
  { t: "9. Contact Us", b: "If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at: hello@akkayglobal.com" },
];

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="21st October 2025">
      <p className="text-lg text-slate-700 leading-relaxed mt-10 mb-12">
        This Privacy Policy for Akkay Tanko Global Services Ltd (doing business as PamaPay App) ("we", "us", or "our") outlines how we collect, store, maintain, and disclose information collected from users ("you", "your") of the PamaPay mobile application ("the App"). This Privacy Policy applies to the PamaPay App and all products and services offered by Akkay Tanko Global Services Ltd.
        <br /><br />
        By accessing or using PamaPay, you agree to the terms of this Privacy Policy.
      </p>

      <div className="space-y-10">
        {SECTIONS.map((s, i) => (
          <section key={i} data-testid={`privacy-section-${i}`}>
            <h2 className="font-display font-bold text-xl sm:text-2xl mb-4 text-slate-900">{s.t}</h2>
            {s.b && <p className="text-slate-700 leading-relaxed">{s.b}</p>}
            {s.list && (
              <ul className="mt-3 space-y-2 text-slate-700">
                {s.list.map((li, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#3B6FBF] flex-shrink-0" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.children && (
              <div className="mt-4 space-y-5 pl-4 border-l-2 border-slate-200">
                {s.children.map((c, k) => (
                  <div key={k}>
                    <h3 className="font-display font-semibold text-base text-slate-900 mb-2">{c.sub}</h3>
                    <p className="text-slate-700 leading-relaxed">{c.b}</p>
                    {c.list && (
                      <ul className="mt-2 space-y-1.5 text-slate-700">
                        {c.list.map((li, m) => (
                          <li key={m} className="flex items-start gap-3">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#3B6FBF] flex-shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}

        <section data-testid="privacy-section-biometric">
          <h2 className="font-display font-bold text-xl sm:text-2xl mb-4 text-slate-900">
            Policy Covering Face and Biometric Data
          </h2>
          <p className="text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">Identity Verification Data.</span>{" "}
            To comply with our Know Your Customer (KYC) obligations, we may collect identity
            verification data, which may include a photograph of your face and government-issued
            identification details. Identity verification is performed through our third-party
            provider, <span className="font-semibold">Dojah Inc.</span>, and the selfie captured
            during verification is transmitted directly to Dojah&apos;s secure systems. We store
            the verification result and a copy of the photograph solely for identity
            verification, fraud prevention and manual review purposes.
          </p>
          <p className="text-slate-700 leading-relaxed mt-4">
            Copies of verification images are automatically deleted{" "}
            <span className="font-semibold">90 days</span> after the verification record is
            created. You may request deletion of your verification data at any time through the{" "}
            <a
              href="/#contact"
              className="text-[#3B6FBF] font-semibold underline underline-offset-2 hover:text-[#2f5ca3]"
              data-testid="privacy-biometric-contact-link"
            >
              contact form on our website
            </a>
            , and we will process such requests in accordance with applicable law.
          </p>
        </section>
      </div>

      <div className="mt-14 p-6 rounded-2xl border border-[#3B6FBF]/30 bg-[#3B6FBF]/5">
        <p className="text-slate-700">
          By using PamaPay, you acknowledge that you have read, understood, and agree to the terms of this Privacy Policy.
        </p>
      </div>
    </LegalLayout>
  );
}
